<template>
  <div class="school-data">
    <div class="school-data__header">
      <div class="school-data__header__left">
        <h1>Fraværstyper fordelt over måneder</h1>
        <changeSchoolYear :white-theme="true" class="marg-1-right"></changeSchoolYear>
      </div>
      <div class="school-data__header__right">
        <p class="d-link" @click="resetDates" v-if="!isDatesPristine">{{ $t('vue2.task_overview.reset') }}</p>
        <Datepickerrange
          :startdate="startDate"
          :enddate="endDate"
          @startchanged="setStartDate"
          @endchanged="setEndDate">
        </Datepickerrange>
      </div>
    </div>
    <div class="loader-container" v-if="isLoading">
      <Loader></Loader>
      <h4>Snup en kop kaffe, det kan tage et par minutter</h4>
    </div>
    <template v-else>
      <div class="compare">
        <label class="compare__toggle">
          <input type="checkbox" v-model="isComparing" @change="readPreviousYearEvents">
          Sammenlign med året før
        </label>
        <div class="compare__chart" v-if="isComparing">
          <Loader v-if="isLoadingPreviousYear"></Loader>
          <LineChart v-else :chart-data="comparisonChartData" :options="chartOptions"></LineChart>
        </div>
      </div>
      <table class="d-table">
        <thead>
        <tr>
          <th></th>
          <th>Årsag</th>
          <th v-for="monthData in restructuredAbsenceData" :key="monthData.month">{{ monthData.month }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="type in visibleAbsenceTypes">
          <td>
            <div class="icon__bg-circle" @click="toggleHiddenAbsence(type)">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="tw-icon">
                <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
                <path fill-rule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 010-1.113zM17.25 12a5.25 5.25 0 11-10.5 0 5.25 5.25 0 0110.5 0z" clip-rule="evenodd" />
              </svg>
          </div>
          </td>
          <td>{{ type }}</td>
          <td v-for="monthData in restructuredAbsenceData" :key="monthData.month">
          {{ monthData[type] }}
        </td>
        </tr>
        <tr class="toggler" @click="showHidden = !showHidden" v-if="hiddenAbsenceTypes.length > 0">
          <td :colspan="2 + (restructuredAbsenceData.length * 1)">
            <p class="hidden-toggler" v-if="!showHidden"><span class="icon-arrow-right4" ></span>Vis skjulte årsager ({{ hiddenAbsenceTypes.length }})</p>
            <p class="hidden-toggler" v-else><span class="icon-arrow-down4"></span>Skjul skjulte årsager</p>
          </td>
        </tr>
        <tr v-for="type in hiddenAbsenceTypes" v-if="showHidden">
          <td>
            <div class="icon__bg-circle" @click="toggleHiddenAbsence(type)">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="tw-icon">
                <path d="M3.53 2.47a.75.75 0 00-1.06 1.06l18 18a.75.75 0 101.06-1.06l-18-18zM22.676 12.553a11.249 11.249 0 01-2.631 4.31l-3.099-3.099a5.25 5.25 0 00-6.71-6.71L7.759 4.577a11.217 11.217 0 014.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113z" />
                <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0115.75 12zM12.53 15.713l-4.243-4.244a3.75 3.75 0 004.243 4.243z" />
                <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 00-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 016.75 12z" />
              </svg>
            </div>
          </td>
          <td>{{ type }}</td>
          <td v-for="monthData in restructuredAbsenceData" :key="monthData.month">
            {{ monthData[type] }}
          </td>
        </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
import ChangeSchoolYear from '../School/ChangeSchoolYear'
import Loader from '../common/Loader'
import moment from 'moment'
import Datepickerrange from '../datepicker/DatePickerRange'
import LineChart from './LineChart'
import schoolDataMixin from '../../mixins/schoolDataMixin'

export default {
  components: {Datepickerrange, Loader, ChangeSchoolYear, LineChart},
  mixins: [schoolDataMixin],
  data () {
    return {
      reportEventTypeFilter: ['absence'],
      showHidden: false,
      hiddenAbsence: [],
      isComparing: false,
      isLoadingPreviousYear: false,
      previousYearEvents: [],
      previousYearLoadedFor: null,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{ticks: {beginAtZero: true}}]
        }
      }
    }
  },
  computed: {
    absenceEvents () {
      return this.events.filter(event => event.event_type === 'absence')
    },
    previousYearStart () {
      return moment(this.startDate).subtract(1, 'year').format('YYYY-MM-DD')
    },
    previousYearEnd () {
      return moment(this.endDate).subtract(1, 'year').format('YYYY-MM-DD')
    },
    comparisonChartData () {
      const current = this.countVisibleAbsencePerMonth(this.absenceEvents, this.startDate)
      const previous = this.countVisibleAbsencePerMonth(this.previousYearEvents, this.previousYearStart)
      return {
        labels: current.map(month => month.label),
        datasets: [
          {
            label: 'Valgte periode',
            data: current.map(month => month.count),
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: false
          },
          {
            label: 'Året før',
            data: previous.map(month => month.count),
            borderColor: '#9ca3af',
            backgroundColor: 'rgba(156, 163, 175, 0.1)',
            borderDash: [5, 5],
            fill: false
          }
        ]
      }
    },
    absenceTypes () {
      const obj = this.restructuredAbsenceData.length > 0 ? this.restructuredAbsenceData[0] : {}
      const arr = Object.keys(obj).filter(key => key !== 'month').sort((a, b) => a.localeCompare(b))
      return arr
    },
    visibleAbsenceTypes () {
      return this.absenceTypes.filter(type => this.hiddenAbsence.indexOf(type) < 0)
    },
    hiddenAbsenceTypes () {
      return this.absenceTypes.filter(type => this.hiddenAbsence.indexOf(type) > -1)
    },
    restructuredAbsenceData () {
      // Convert start and end dates to moment objects
      let start = moment(this.startDate)
      let end = moment(this.endDate)

      // Initialize an empty array to store the restructured data
      let restructuredData = []

      // Initialize an empty object to store unique absence types
      let absenceTypes = {}

      // Initialize the restructuredData array with objects for each month
      while (start.isSameOrBefore(end)) {
        restructuredData.push({
          month: start.format('MMM YYYY')
        })
        start.add(1, 'month')
      }

      // Iterate over each event in the absenceEvents array
      this.absenceEvents.forEach(event => {
        // Extract the month from the event start date
        let eventDate = moment(event.start)
        let monthIndex = restructuredData.findIndex(data => data.month === eventDate.format('MMM YYYY'))

        // Check if the event falls within the start and end dates
        if (monthIndex !== -1) {
          // Extract the absence type from the event title
          let absenceType = event.title

          // Check if this absence type already exists in our absenceTypes object
          if (!absenceTypes[absenceType]) {
            // If not, add it to the absenceTypes object and initialize it in each month object
            absenceTypes[absenceType] = true
            restructuredData.forEach(data => {
              data[absenceType] = 0
            })
          }

          // Increment the count for this absence type in the correct month object
          restructuredData[monthIndex][absenceType]++
        }
      })

      // Set the restructured data as a new data property
      return restructuredData
    }
  },
  methods: {
    toggleHiddenAbsence (type) {
      const idx = this.hiddenAbsence.indexOf(type)
      if (idx < 0) {
        this.hiddenAbsence.push(type)
      } else {
        this.hiddenAbsence.splice(idx, 1)
      }
      this.$helpers.setLocalStorage('d_hidden_absence', JSON.stringify(this.hiddenAbsence))
    },
    readPreviousYearEvents () {
      const period = this.previousYearStart + '|' + this.previousYearEnd
      if (!this.isComparing || this.previousYearLoadedFor === period) {
        return
      }
      const calendarIds = this.reportCalendars.map(calendar => calendar.id)
      if (calendarIds.length < 1) {
        return
      }
      this.previousYearLoadedFor = period
      this.isLoadingPreviousYear = true
      this.fetchEvents(calendarIds, this.previousYearStart, this.previousYearEnd, ['absence'], events => {
        if (this.previousYearLoadedFor !== period) {
          return
        }
        this.previousYearEvents = Object.freeze(events)
        this.isLoadingPreviousYear = false
      })
    },
    countVisibleAbsencePerMonth (events, startDate) {
      const months = []
      const month = moment(startDate).startOf('month')
      const monthCount = moment(this.endDate).startOf('month').diff(moment(this.startDate).startOf('month'), 'months')
      const lastMonth = moment(startDate).startOf('month').add(monthCount, 'months')
      while (month.isSameOrBefore(lastMonth, 'month')) {
        months.push({key: month.format('YYYY-MM'), label: month.format('MMM'), count: 0})
        month.add(1, 'month')
      }
      events.forEach(event => {
        if (this.hiddenAbsence.indexOf(event.title) > -1) {
          return
        }
        const item = months.find(month => month.key === moment(event.start).format('YYYY-MM'))
        if (item) {
          item.count += 1
        }
      })
      return months
    }
  },
  watch: {
    startDate () {
      this.readPreviousYearEvents()
    },
    endDate () {
      this.readPreviousYearEvents()
    }
  },
  mounted () {
    const localVal = this.$helpers.getLocalStorage('d_hidden_absence')
    if (localVal) {
      this.hiddenAbsence = JSON.parse(localVal)
    }
  }
}
</script>


<style scoped>
@import "../../assets/css/variables.css";

.school-data {
  margin: 1rem;
}

.school-data__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1440px;
  min-width: 1100px;
}

.school-data__header__left {
  display: flex;
  align-items: center;
}

.school-data__header h1 {
  margin-right: 1rem;
}

.loader-container {
  text-align: center;
}

.compare {
  margin-bottom: 1rem;
}

.compare__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.compare__chart {
  position: relative;
  height: 300px;
  margin-top: 1rem;
  padding: 1rem;
  background: #fff;
  border: 1px solid #d8d8d8;
  border-radius: 4px;
}

.school-data__header__right {
  display: flex;
  align-items: center;
}


.d-link {
  color: var(--blue);
  cursor: pointer;
  margin-right: 1rem;
}

.d-link:hover {
  text-decoration: underline;
}


.d-table {
  width: 100%;
  border: 1px solid #d8d8d8;
  border-collapse: collapse;
  border-radius: 4px;
}

.d-table thead {
  border-bottom: 1px solid #d8d8d8;
  background: #f6f9fc;
  color: #728195;
  text-transform: uppercase;
  border-radius: 4px;
  font-size: 11px;
}

.d-table thead th {
  padding: 0.5rem 1rem;
  text-align: left;
}

.d-table tbody tr {
  width: 100%;
  background: #f9fafb;
  border-bottom: 1px solid #e3e8ee;
}

.d-table tbody tr:nth-child(odd) {
  background: #ffffff;
}

.d-table tbody tr:hover {
  background: aliceblue;
}

.d-table tbody tr td {
  padding: 0.5rem 1rem;
  text-align: left;
}

.hidden-toggler {
  cursor: pointer;
  padding: 0.25rem 1rem;
}

.hidden-toggler > span {
  margin-right: 0.5rem;
}

.d-table thead tr th:first-child,
.d-table tbody tr td:first-child {
  max-width: 50px;
  width: 50px;
}

.toggler {
  border-bottom: 1px solid #d8d8d8;
  background: #eef0f5 !important;
  color: #728195;
  text-transform: uppercase;
  font-weight: bold;
  font-size: 11px;
}

.toggler td {
  padding: 0;
}
</style>



// WEBPACK FOOTER //
// SchoolDataAbsence.vue?709c814a